import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import './reset-password.css';
import HeaderSection from '../../../../components/layout/header';
import useNavigation from '../../../../shared/utils/useNavigation';
import qs from "qs";
import { resetPassword, verifyResetToken } from '../../../../shared/actions/authActions';
import { showError, showSuccess } from '../../../../shared/utils/swalHandler';
import { createResetPasswordFormSchema } from '../../../../shared/validations/auth';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "../../../../shared/components/form";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { CenteredLoader } from '../../../../shared/utils/chatUtil';

const ResetPassword = () => {
  const navigate = useNavigation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [tokenValid, setTokenValid] = useState(null); // null = checking, true = valid, false = invalid

  const queryParams = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });
  const token = queryParams.token;

  // Verify token via API on mount
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      showError(
        'Invalid Link',
        'Token is required. Please use the link from your reset password email.'
      ).then(() => navigate.toLogin());
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const result = await dispatch(verifyResetToken(token));
        if (cancelled) return;
        const valid = result?.valid === true;
        setTokenValid(valid);
        if (!valid) {
          const title = result?.expired ? 'Link Expired' : 'Invalid Link';
          const message = result?.message || (result?.expired
            ? 'This reset link has expired. Please request a new one.'
            : 'Invalid or expired link.');
          showError(title, message).then(() => navigate.toLogin());
        }
      } catch (err) {
        if (cancelled) return;
        setTokenValid(false);
        showError('Error', err?.message || 'Unable to verify link. Please try again.').then(() => navigate.toLogin());
      }
    })();
    return () => { cancelled = true; };
  }, [token, dispatch]);

  const form = useForm({
    resolver: zodResolver(createResetPasswordFormSchema()),
    defaultValues: {
      password: '',
      confirmPassword: ''
    },
    mode: "onChange",
  });

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      const response = await dispatch(resetPassword({
        token: token,
        new_password: data.password,
      }));

      if (response.success) {
        await showSuccess('Password Reset Successfully!', 'Your password has been updated successfully. You can now log in with your new password.');
        navigate.toLogin();
      } else {
        await showError('Password Reset Failed', response.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      const errorMessage = "An unexpected error occurred. Please try again.";
      await showError('Password Reset Failed', error?.message || errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate.toLogin();
  };

  return (
    <div className={`modern-reset-password-screen`}>
      {/* Header */}
      <HeaderSection
        type="image"
        handleBack={handleBack}
      />



      <main className="modern-reset-password-main">
        <div className="modern-reset-password-bg-effects">
          <div className="modern-reset-password-bg-gradient"></div>
          <div className="modern-reset-password-bg-blur-1"></div>
          <div className="modern-reset-password-bg-blur-2"></div>
        </div>

        <div className="modern-reset-password-container">
          {tokenValid === null ? (
            <CenteredLoader size={48} text="Verifying link..." />
          ) : tokenValid === false ? (
            <CenteredLoader size={48} text="Redirecting..." />
          ) : (
            <>
              <div className="modern-reset-password-welcome">
                <div className="profile-tags-icon">
                  <i className="fas fa-lock"></i>
                </div>
                <h1 className="modern-reset-password-welcome-title">
                  Reset Password
                </h1>
                <p className="modern-reset-password-welcome-subtitle">
                  Create a new password to regain access to your account.
                </p>
                <div className="modern-reset-password-welcome-divider"></div>
              </div>

              <div className="modern-reset-password-form-container">
                <div className="modern-reset-password-form-wrapper">
                  <Form {...form}>
                    <form className="modern-reset-password-form" onSubmit={form.handleSubmit(onSubmit)}>
                      <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem className="modern-reset-password-input-group">
                            <FormLabel className="modern-reset-password-label">New Password</FormLabel>
                            <div className="modern-reset-password-input-wrapper">
                              <div className="modern-reset-password-input-icon">
                                <i className="fas fa-lock"></i>
                              </div>
                              <FormControl>
                                <input
                                  type={showPassword ? 'text' : 'password'}
                                  className="modern-reset-password-input"
                                  placeholder="Password"
                                  autoComplete="new-password"
                                  minLength="6"
                                  {...field}
                                />
                              </FormControl>
                              <div className="modern-login-password-toggle" onClick={() => setShowPassword(!showPassword)}>
                                {/* <i className={`far ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i> */}
                                <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} />
                              </div>
                              <div className="modern-reset-password-input-shimmer"></div>
                            </div>
                            {form.watch('password') && (
                              <FormMessage
                                className="fas fa-exclamation-triangle"
                              />
                            )}
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="confirmPassword"
                        render={({ field }) => (
                          <FormItem className="modern-reset-password-input-group">
                            <FormLabel className="modern-reset-password-label">Confirm Password</FormLabel>
                            <div className="modern-reset-password-input-wrapper">
                              <div className="modern-reset-password-input-icon">
                                <i className="fas fa-lock"></i>
                              </div>
                              <FormControl>
                                <input
                                  type={showPassword2 ? 'text' : 'password'}
                                  className="modern-reset-password-input"
                                  placeholder="Confirm password"
                                  autoComplete="new-password"
                                  minLength="6"
                                  {...field}
                                />
                              </FormControl>
                              <div className="modern-login-password-toggle" onClick={() => setShowPassword2(!showPassword2)}>
                                {/* <i className={`far ${showPassword2 ? 'fa-eye-slash' : 'fa-eye'}`}></i> */}
                                <FontAwesomeIcon icon={showPassword2 ? faEye : faEyeSlash} />
                              </div>
                              <div className="modern-reset-password-input-shimmer"></div>
                            </div>
                            {form.watch('confirmPassword') && (
                              <FormMessage />
                            )}
                          </FormItem>
                        )}
                      />

                      <button
                        type="submit"
                        className="modern-reset-password-submit-btn"
                        disabled={isLoading || form.formState.isSubmitting || !form.formState.isValid}
                      >
                        {isLoading || form.formState.isSubmitting ? (
                          <span className="modern-reset-password-btn-loading">
                            <i className="fas fa-spinner fa-spin"></i>
                            <span>Resetting...</span>
                          </span>
                        ) : (
                          <span className="modern-reset-password-btn-content">
                            <i className="fas fa-key"></i>
                            <span>Reset Password</span>
                          </span>
                        )}
                        <div className="modern-reset-password-btn-shimmer"></div>
                      </button>
                    </form>
                  </Form>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};
export default ResetPassword;
